import { hasGame, S } from '../core/game';
import type { GardenPlot } from '../core/state';
import { arePlantsProtected, canLeft, stage } from '../content/garden';
import { rainedToday } from '../content/weather';
import type { Decor, Rect } from '../world/mapdef';
import { skyTint } from './lighting';
import { wind } from './nature';
import { OUTLINE, block, ellipse, hash, rect, shade, shadow, type Ctx } from './paint';
import { registerPropDrawers } from './propRegistry';

/**
 * Family package art: the balcony (rooftop view, railings, facade below, laundry,
 * washing machine, pots), the nine garden planters with growing plants, and the tap.
 */

const SCALE = 3;
const caches = new Map<string, HTMLCanvasElement>();

/** Paint a static layer once into an offscreen canvas (logical rect x0,y0,w,h) and blit it. */
function cached(c: Ctx, key: string, x0: number, y0: number, w: number, h: number, paint: (c: Ctx) => void): void {
  let cv = caches.get(key);
  if (!cv) {
    cv = document.createElement('canvas');
    cv.width = Math.ceil(w * SCALE);
    cv.height = Math.ceil(h * SCALE);
    const cc = cv.getContext('2d')!;
    cc.scale(SCALE, SCALE);
    cc.translate(-x0, -y0);
    paint(cc);
    caches.set(key, cv);
  }
  c.drawImage(cv, x0, y0, w, h);
}

const MAP_W = 256, FLOOR_Y = 32, FRONT_Y = 160;
const VX0 = -140, VX1 = MAP_W + 140, SKY_TOP = -340;

// --- Far view -------------------------------------------------------------------------

function mosaic(c: Ctx, x: number, y: number, w: number, h: number, base: string, tw = 3, th = 1.6): void {
  rect(c, base, x, y, w, h);
  for (let j = 0, row = 0; j < h; j += th, row++) {
    rect(c, shade(base, -0.12), x, y + j, w, 0.3);
    for (let i = (row % 2) * (tw / 2); i < w; i += tw) {
      rect(c, shade(base, -0.1), x + i, y + j, 0.3, th);
      const k = hash(Math.floor((x + i) * 3), Math.floor((y + j) * 3), 7);
      if (k > 0.85) rect(c, shade(base, k > 0.93 ? 0.08 : -0.05), x + i + 0.3, y + j + 0.3, tw - 0.6, th - 0.6);
    }
  }
}

function waterTower(c: Ctx, x: number, y: number, kind: number): void {
  // Four legs, then a stainless drum (or a blue plastic tank) — every Taiwanese rooftop has one.
  for (const dx of [-4, -1.5, 1.5, 4]) rect(c, '#5b5f63', x + dx - 0.3, y - 5, 0.7, 5);
  rect(c, '#5b5f63', x - 4.5, y - 3, 9, 0.6);
  if (kind) {
    block(c, x - 5, y - 13, 10, 8, '#3d7dc8', '#62a0e2', '#2a5c99');
    ellipse(c, '#5a96d8', x, y - 13, 5, 1.6);
  } else {
    block(c, x - 5, y - 14, 10, 9, '#cfd6db', '#eef2f4', '#9aa3a9');
    for (let k = 0; k < 3; k++) rect(c, 'rgba(90,100,110,0.35)', x - 4.5, y - 12 + k * 3, 9, 0.5);
    ellipse(c, '#e2e7ea', x, y - 14, 5, 1.6);
    rect(c, 'rgba(255,255,255,0.7)', x - 3, y - 13, 1, 7);
  }
}

function ironShed(c: Ctx, x: number, y: number, w: number, col: string): void {
  // Corrugated metal rooftop shed (鐵皮屋).
  block(c, x, y - 8, w, 8, shade(col, -0.25), undefined, undefined);
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(x - 1, y - 8);
  c.lineTo(x + w / 2, y - 13);
  c.lineTo(x + w + 1, y - 8);
  c.closePath();
  c.fill();
  for (let i = 1; i < w; i += 2) rect(c, shade(col, -0.18), x + i, y - 12 + Math.abs(i - w / 2) * (5 / (w / 2)) * 1 - 0.2, 0.5, 5);
  rect(c, '#2b2f33', x + w / 2 - 2, y - 6, 4, 6);
}

/** Windows that light up at night (collected while painting the view). */
const LIT: [number, number, number, number][] = [];

function building(c: Ctx, x: number, base: number, w: number, h: number, col: string, seed: number, far: boolean): void {
  const top = base - h;
  rect(c, shade(col, -0.35), x - 0.4, top - 0.4, w + 0.8, h + 0.8);
  mosaic(c, x, top, w, h, col, far ? 4 : 3, far ? 2 : 1.6);
  rect(c, shade(col, 0.18), x, top, w, 1.2); // parapet cap
  rect(c, 'rgba(0,0,0,0.12)', x + w - 2, top, 2, h); // side shade
  // Windows with iron grilles and AC boxes.
  const ww = far ? 3 : 4, wh = far ? 3 : 4;
  for (let j = top + 5; j < base - 4; j += far ? 7 : 9) {
    for (let i = x + 3; i < x + w - ww - 1; i += far ? 6 : 8) {
      const k = hash(Math.floor(i), Math.floor(j), seed);
      if (k < 0.12) continue;
      rect(c, k > 0.8 ? '#7d9bb0' : '#4b5866', i, j, ww, wh);
      if (k > 0.5 && k < 0.8) LIT.push([i, j, ww, wh]);
      rect(c, 'rgba(255,255,255,0.25)', i, j, ww, 0.6);
      if (!far) {
        for (let b = 0.8; b < ww; b += 1.3) rect(c, 'rgba(235,235,230,0.55)', i + b, j, 0.35, wh);
        if (k > 0.66) block(c, i + ww + 0.2, j + 1.5, 3, 2.6, '#e7e8e4', undefined, undefined);
      }
    }
  }
  // Rooftop clutter.
  const r = hash(seed, w, 3);
  if (r < 0.45) waterTower(c, x + w * (0.25 + r), top, (seed + w) % 3 === 0 ? 1 : 0);
  else if (r < 0.75 && w > 16) ironShed(c, x + 2, top, Math.min(w - 4, 16), ['#4f8f9a', '#3d6fa8', '#c0603a', '#6a9a5a'][seed % 4]);
  else {
    rect(c, '#5b5f63', x + w * 0.6, top - 10, 0.5, 10);
    rect(c, '#5b5f63', x + w * 0.6 - 3, top - 8, 6, 0.4);
    rect(c, '#5b5f63', x + w * 0.6 - 2, top - 6, 4, 0.4);
  }
}

function templeRoof(c: Ctx, cx: number, base: number): void {
  // A small temple between the blocks: orange tiles, curved swallowtail ridge, a pearl on top.
  block(c, cx - 14, base - 14, 28, 14, '#c9473a', '#e0675a', '#932d24');
  for (let i = -12; i < 13; i += 3) rect(c, '#e9a23b', cx + i, base - 11, 1.2, 2);
  c.fillStyle = '#c8572a';
  c.beginPath();
  c.moveTo(cx - 22, base - 16);
  c.quadraticCurveTo(cx - 12, base - 21, cx, base - 21);
  c.quadraticCurveTo(cx + 12, base - 21, cx + 22, base - 16);
  c.lineTo(cx + 18, base - 13);
  c.lineTo(cx - 18, base - 13);
  c.closePath();
  c.fill();
  for (let i = -17; i < 18; i += 2) rect(c, 'rgba(120,40,20,0.45)', cx + i, base - 19 + Math.abs(i) * 0.12, 0.5, 5);
  c.strokeStyle = '#3f8f5a';
  c.lineWidth = 1.2;
  c.beginPath();
  c.moveTo(cx - 22, base - 17);
  c.quadraticCurveTo(cx - 12, base - 22.5, cx, base - 22.5);
  c.quadraticCurveTo(cx + 12, base - 22.5, cx + 22, base - 17);
  c.stroke();
  // Swallowtail tips and the pearl.
  rect(c, '#3f8f5a', cx - 24, base - 21, 2, 4);
  rect(c, '#3f8f5a', cx + 22, base - 21, 2, 4);
  ellipse(c, '#f2c94c', cx, base - 25, 2, 2);
  ellipse(c, '#fff2b0', cx - 0.6, base - 25.6, 0.7, 0.7);
}

function paintView(c: Ctx): void {
  // Sky.
  const g = c.createLinearGradient(0, SKY_TOP, 0, 10);
  g.addColorStop(0, '#78b8e6');
  g.addColorStop(0.55, '#b9def2');
  g.addColorStop(1, '#eef4ea');
  c.fillStyle = g;
  c.fillRect(VX0, SKY_TOP, VX1 - VX0, FLOOR_Y + 10 - SKY_TOP);
  // Mountains on the horizon (Taiwan is never far from them).
  for (const [base, amp, col, seed] of [[-78, 42, '#a8c3d8', 1], [-62, 30, '#93b4c9', 2]] as const) {
    c.fillStyle = col;
    c.beginPath();
    c.moveTo(VX0, base + 20);
    for (let x = VX0; x <= VX1; x += 4) {
      const n = Math.sin(x * 0.021 + seed) * 0.5 + Math.sin(x * 0.057 + seed * 3) * 0.3 + Math.sin(x * 0.11) * 0.12;
      c.lineTo(x, base - (n * 0.5 + 0.5) * amp);
    }
    c.lineTo(VX1, base + 20);
    c.closePath();
    c.fill();
  }
  ellipse(c, 'rgba(255,255,255,0.35)', 60, -60, 220, 14);
  // Far blocks (hazy), the temple, then nearer rooftops right behind the railing.
  let x = VX0;
  let seed = 11;
  while (x < VX1) {
    const w = 18 + Math.floor(hash(seed, 1, 5) * 26);
    const h = 26 + Math.floor(hash(seed, 2, 5) * 52);
    const col = ['#cfc7b8', '#bfc6cb', '#d8ccb4', '#c4beb4', '#b9c3bf'][seed % 5];
    building(c, x, -10, w, h, col, seed, true);
    x += w + 1 + Math.floor(hash(seed, 3, 5) * 4);
    seed++;
  }
  rect(c, 'rgba(225,238,246,0.38)', VX0, SKY_TOP, VX1 - VX0, -10 - SKY_TOP);
  templeRoof(c, 70, 4);
  x = VX0 - 6;
  seed = 40;
  while (x < VX1) {
    const w = 22 + Math.floor(hash(seed, 1, 9) * 26);
    const h = 14 + Math.floor(hash(seed, 2, 9) * 26);
    const col = ['#e3d6bd', '#d4cfc4', '#cdb79a', '#dcd2c0', '#c9cfcf'][seed % 5];
    if (Math.abs(x + w / 2 - 70) > 24) building(c, x, 22, w, h, col, seed, false);
    x += w + 2 + Math.floor(hash(seed, 3, 9) * 6);
    seed++;
  }
  // A rooftop laundry line and potted plants on the nearest roof.
  c.strokeStyle = 'rgba(60,60,60,0.6)';
  c.lineWidth = 0.4;
  c.beginPath();
  c.moveTo(150, 4);
  c.quadraticCurveTo(165, 7, 180, 4);
  c.stroke();
  ['#f2f2f2', '#e96b5b', '#5b8fd9', '#f2d24c'].forEach((col, i) => rect(c, col, 154 + i * 6, 5 + Math.sin(i) * 0.6, 3.5, 4));

  // Our building on the left (beige mosaic tiles, windows with grilles).
  mosaic(c, VX0, SKY_TOP, -VX0, FRONT_Y + 20 - SKY_TOP, '#d9c7a4');
  rect(c, 'rgba(0,0,0,0.12)', -6, SKY_TOP, 6, FRONT_Y + 20 - SKY_TOP);
  for (let j = SKY_TOP + 20; j < -10; j += 46) {
    for (const i of [-120, -70]) {
      block(c, i, j, 30, 22, '#5a6876', '#7d91a3', '#3e4954');
      for (let b = 2; b < 30; b += 3) rect(c, 'rgba(240,240,236,0.7)', i + b, j, 0.6, 22);
      rect(c, 'rgba(240,240,236,0.7)', i, j + 11, 30, 0.6);
      block(c, i + 32, j + 10, 10, 8, '#ecebe6', '#ffffff', '#c3c2bc');
      for (let k = 0; k < 4; k++) rect(c, '#9a9a94', i + 33, j + 12 + k * 1.5, 8, 0.4);
    }
  }
  // Neighbour's wall across the gap on the right.
  mosaic(c, MAP_W, FLOOR_Y - 6, VX1 - MAP_W, FRONT_Y + 30 - FLOOR_Y, '#b9b2a6');
  rect(c, 'rgba(20,20,30,0.25)', MAP_W, FLOOR_Y - 6, VX1 - MAP_W, FRONT_Y + 30 - FLOOR_Y);
  for (let j = FLOOR_Y + 8; j < FRONT_Y; j += 40) {
    block(c, MAP_W + 22, j, 26, 18, '#56616c', '#6f7d8a', '#3b444d');
    for (let b = 2; b < 26; b += 3) rect(c, 'rgba(230,230,226,0.6)', MAP_W + 22 + b, j - 2, 0.6, 22);
    block(c, MAP_W + 52, j + 8, 11, 8, '#e4e3de', '#ffffff', '#b9b8b2');
  }

  // Back railing: aluminium handrail and bars over a tiled concrete ledge.
  mosaic(c, 0, 24, MAP_W, FLOOR_Y - 24 + 1, '#e6d9bf', 2.6, 1.4);
  rect(c, '#f4ead6', 0, 23, MAP_W, 1.4);
  rect(c, 'rgba(0,0,0,0.22)', 0, FLOOR_Y, MAP_W, 1.2);
  for (let i = 2; i < MAP_W; i += 4) {
    rect(c, '#8f989e', i, 11, 1, 12);
    rect(c, 'rgba(255,255,255,0.45)', i, 11, 0.35, 12);
  }
  for (let i = 0; i < MAP_W; i += 32) block(c, i + 0.5, 8, 2.5, 16, '#b8c0c5', '#e2e7ea', '#8f989e');
  block(c, 0, 8, MAP_W, 3, '#c9d0d4', '#eef2f4', '#959ea4');

  // Side railing on the right (x 240–256): ledge top, posts and the rail seen from above.
  const sx = MAP_W - 16;
  rect(c, '#cfc3ab', sx, 20, 16, FRONT_Y - 18);
  mosaic(c, sx + 9, 20, 7, FRONT_Y - 18, '#e6d9bf', 2.6, 1.4);
  rect(c, 'rgba(0,0,0,0.18)', sx, 20, 1.2, FRONT_Y - 18);
  for (let j = 24; j < FRONT_Y; j += 4) rect(c, '#8f989e', sx + 4, j, 4, 0.8);
  for (let j = 24; j < FRONT_Y; j += 16) block(c, sx + 3.5, j - 1, 5, 3, '#b8c0c5', '#e2e7ea', '#8f989e');
  block(c, sx + 5, 18, 2.6, FRONT_Y - 18, '#c9d0d4', '#eef2f4', '#959ea4');

  // The wall of our flat (column 0) with the sliding door and a gas water heater.
  rect(c, '#c9b896', 0, 0, 16, FRONT_Y + 16);
  mosaic(c, 0, 0, 13, FRONT_Y + 16, '#d9c7a4');
  rect(c, 'rgba(0,0,0,0.2)', 13, 0, 3, FRONT_Y + 16);
  rect(c, 'rgba(255,255,255,0.25)', 12.4, 0, 0.6, FRONT_Y + 16);
  // Aluminium sliding door (48–64) with warm light from the living room.
  block(c, 2, 44, 13, 22, '#aeb6bb', '#dfe4e7', '#7c858b');
  const dg = c.createLinearGradient(0, 46, 0, 64);
  dg.addColorStop(0, '#ffe2a8');
  dg.addColorStop(1, '#f2b46a');
  c.fillStyle = dg;
  c.fillRect(4, 46, 9, 18);
  rect(c, 'rgba(255,255,255,0.4)', 5, 47, 1, 15);
  rect(c, '#7c858b', 8.2, 46, 0.8, 18);
  rect(c, '#5b5f63', 11, 54, 1, 3);
  // Doormat on the floor.
  block(c, 17, 50, 11, 12, '#7a4b2a', '#946039', '#5a3420');
  for (let j = 52; j < 61; j += 2) rect(c, 'rgba(255,220,160,0.25)', 18.5, j, 8, 0.5);
  // Wall lamp above the door.
  block(c, 5, 37, 6, 4, '#fff4d0', '#ffffff', '#d9c48e');
  ellipse(c, 'rgba(255,230,160,0.35)', 8, 41, 8, 5);
  // Gas water heater (熱水器) with its pipes.
  block(c, 1, 68, 12, 18, '#f2f1ec', '#ffffff', '#c9c8c0');
  rect(c, '#2b8fd0', 4, 72, 6, 2.5);
  ellipse(c, '#e05a3a', 7, 79, 1.2, 1.2);
  rect(c, '#9aa0a6', 3, 86, 1, 8);
  rect(c, '#c87a3a', 9, 86, 1, 10);
  rect(c, '#9aa0a6', 6, 62, 1, 6);
}

/** Lit windows at night, drawn over the cached view with the night amount. */
function paintNightWindows(c: Ctx): void {
  LIT.forEach(([x, y, w, h], i) => {
    ellipse(c, 'rgba(255,200,110,0.22)', x + w / 2, y + h / 2, w + 1.5, h + 1);
    rect(c, i % 3 ? '#ffd98a' : '#fff1c4', x, y, w, h);
  });
  for (let j = SKY_TOP + 20; j < -10; j += 46) for (const i of [-120, -70]) if (hash(i, j, 9) > 0.4) rect(c, 'rgba(255,214,140,0.75)', i + 1, j + 1, 28, 20);
}

function birds(c: Ctx, t: number): void {
  const k = (t * 0.035) % 1;
  if (k > 0.6) return;
  const bx = VX0 + (k / 0.6) * (VX1 - VX0 + 40), by = -150 + Math.sin(t * 0.4) * 10;
  c.strokeStyle = 'rgba(40,40,50,0.7)';
  c.lineWidth = 0.6;
  for (let i = 0; i < 3; i++) {
    const x = bx - i * 9, y = by + i * 4 + (i % 2) * 2;
    const f = Math.sin(t * 9 + i * 2) * 1.6;
    c.beginPath();
    c.moveTo(x - 2.5, y - f);
    c.lineTo(x, y);
    c.lineTo(x + 2.5, y - f);
    c.stroke();
  }
}

function drawView(c: Ctx, t: number): void {
  cached(c, 'view', VX0, SKY_TOP, VX1 - VX0, FRONT_Y + 30 - SKY_TOP, paintView);
  const night = hasGame() ? skyTint(S().time).night : 0;
  if (night > 0.05) {
    c.globalAlpha = night;
    cached(c, 'viewNight', VX0, SKY_TOP, VX1 - VX0, 40 - SKY_TOP, paintNightWindows);
    c.globalAlpha = 1;
  } else birds(c, t);
}

// --- Front railing and the facade below ----------------------------------------------

function paintFront(c: Ctx): void {
  const y0 = FRONT_Y;
  // The facade going down: tiles, the balconies below with iron grilles, AC units, plants.
  mosaic(c, VX0, y0 + 12, VX1 - VX0, 360, '#d4c19c');
  for (let j = y0 + 34; j < y0 + 360; j += 70) {
    for (let i = 12; i < MAP_W - 20; i += 76) {
      const k = hash(i, j, 4);
      block(c, i, j, 58, 30, '#4e5a66', '#6a7886', '#36404a');
      // 鐵窗: white iron grille with a little curl pattern.
      for (let b = 2; b < 58; b += 4) rect(c, 'rgba(240,240,232,0.85)', i + b, j - 2, 0.7, 34);
      rect(c, 'rgba(240,240,232,0.85)', i - 1, j - 2, 60, 1);
      rect(c, 'rgba(240,240,232,0.85)', i - 1, j + 31, 60, 1);
      if (k > 0.4) for (let p = 0; p < 3; p++) ellipse(c, ['#4f8f4a', '#6aa85a', '#3f7a3d'][p], i + 8 + p * 7, j + 26, 4, 3);
      if (k > 0.55) ['#f2f2f2', '#e9a0b0', '#7fa8e0'].forEach((col, q) => rect(c, col, i + 30 + q * 7, j + 4, 5, 8));
      block(c, i + 62, j + 14, 12, 10, '#ecebe6', '#ffffff', '#bebdb6');
      ellipse(c, '#9a9a94', i + 68, j + 19, 3, 3);
      rect(c, 'rgba(0,0,0,0.15)', i - 1, j + 33, 60, 3);
    }
  }
  const dark = c.createLinearGradient(0, y0 + 12, 0, y0 + 370);
  dark.addColorStop(0, 'rgba(30,24,30,0.12)');
  dark.addColorStop(0.5, 'rgba(30,24,30,0.55)');
  dark.addColorStop(1, 'rgba(30,24,30,0.85)');
  c.fillStyle = dark;
  c.fillRect(VX0, y0 + 12, VX1 - VX0, 360);
  // The ledge: tiled concrete with a light cap and a drip shadow.
  mosaic(c, 0, y0 + 1, MAP_W, 12, '#e6d9bf', 2.6, 1.4);
  rect(c, '#f4ead6', 0, y0, MAP_W, 1.6);
  rect(c, 'rgba(0,0,0,0.3)', 0, y0 + 12, MAP_W, 2);
  rect(c, 'rgba(0,0,0,0.2)', 0, y0 - 1, MAP_W, 1);
  // Bars and the handrail (low, so you can see the plants behind).
  for (let i = 18; i < MAP_W - 14; i += 4) {
    rect(c, '#8f989e', i, y0 - 11, 1, 11);
    rect(c, 'rgba(255,255,255,0.45)', i, y0 - 11, 0.35, 11);
  }
  for (let i = 16; i < MAP_W - 12; i += 32) block(c, i, y0 - 14, 2.5, 14, '#b8c0c5', '#e2e7ea', '#8f989e');
  block(c, 14, y0 - 14, MAP_W - 26, 3, '#c9d0d4', '#eef2f4', '#959ea4');
}

// --- Balcony props -------------------------------------------------------------------

function cloth(c: Ctx, x: number, y: number, w: number, h: number, col: string, sw: number, kind: number): void {
  // Hangs from the bar at (x, y); `sw` swings the hem.
  c.fillStyle = shade(col, -0.45);
  c.beginPath();
  c.moveTo(x - 0.4, y);
  c.lineTo(x + w + 0.4, y);
  c.lineTo(x + w + sw + 0.4, y + h + 0.4);
  c.lineTo(x + sw - 0.4, y + h + 0.4);
  c.closePath();
  c.fill();
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(x, y);
  c.lineTo(x + w, y);
  c.lineTo(x + w + sw, y + h);
  c.lineTo(x + sw, y + h);
  c.closePath();
  c.fill();
  if (kind === 1) {
    // T-shirt sleeves.
    rect(c, col, x - 2 + sw * 0.2, y, 2, 3);
    rect(c, col, x + w + sw * 0.2, y, 2, 3);
    rect(c, 'rgba(255,255,255,0.6)', x + w / 2 - 1 + sw * 0.5, y + 3, 2, 2);
  } else if (kind === 2) {
    rect(c, 'rgba(255,255,255,0.35)', x + sw * 0.5, y + h * 0.6, w, 0.8);
    rect(c, 'rgba(255,255,255,0.35)', x + sw * 0.7, y + h * 0.75, w, 0.8);
  } else if (kind === 3) rect(c, 'rgba(0,0,0,0.2)', x + w / 2 + sw * 0.5 - 0.3, y + 2, 0.6, h - 2);
  rect(c, 'rgba(255,255,255,0.2)', x, y, w, 0.8);
  rect(c, '#e8e3d6', x + 0.5, y - 1, 1, 2);
  rect(c, '#e8e3d6', x + w - 1.5, y - 1, 1, 2);
}

function drawLaundry(c: Ctx, d: Decor, t: number): void {
  const { x, y } = d;
  const w = d.w ?? 80;
  const x0 = x - w / 2 + 4, x1 = x + w / 2 - 4;
  shadow(c, x, y, w / 2, 3, 0.22);
  const top = y - 30;
  // Stainless A-frame legs.
  for (const lx of [x0, x1]) {
    rect(c, '#9aa3a9', lx - 3, y - 1, 6, 1.2);
    c.strokeStyle = '#b8c0c5';
    c.lineWidth = 1;
    c.beginPath();
    c.moveTo(lx - 3, y);
    c.lineTo(lx, top);
    c.lineTo(lx + 3, y);
    c.stroke();
  }
  const g = wind.at(x, y);
  const sw = (k: number) => (g * 2.2 + Math.sin(t * 2.2 + k) * 0.8) * 0.9;
  cloth(c, x0 + 4, top + 1, 12, 13, '#f4f4f0', sw(0), 1); // school PE shirt
  rect(c, '#2f6fbf', x0 + 6 + sw(0) * 0.6, top + 10, 8, 1.5);
  cloth(c, x0 + 19, top + 1, 9, 17, '#f3a7b8', sw(1), 2); // towel
  cloth(c, x0 + 31, top + 1, 10, 15, '#3b5f9e', sw(2), 3); // jeans
  cloth(c, x0 + 44, top + 1, 9, 11, '#ffd34e', sw(3), 1); // little sister's shirt
  // Round sock hanger (八爪夾) swinging from a hook.
  const hx = x1 - 10 + sw(4) * 0.4;
  rect(c, '#9aa3a9', x1 - 10.3, top, 0.6, 4);
  ellipse(c, 'rgba(0,0,0,0)', hx, top + 5, 6, 1.5);
  c.strokeStyle = '#4fb0d8';
  c.lineWidth = 0.8;
  c.beginPath();
  c.ellipse(hx, top + 5, 6, 1.5, 0, 0, Math.PI * 2);
  c.stroke();
  ['#ffffff', '#ff8a8a', '#ffffff', '#8ad08a'].forEach((col, i) => {
    const sx = hx - 5 + i * 3.2 + sw(5 + i) * 0.5;
    rect(c, '#4fb0d8', sx + 0.6, top + 5, 0.5, 1.5);
    block(c, sx, top + 6.5, 2.2, 5, col);
    rect(c, col, sx + 0.8, top + 10.5, 2, 1.6);
  });
  // The top bar.
  block(c, x0 - 1, top - 1, x1 - x0 + 2, 2.2, '#c9d0d4', '#eef2f4', '#959ea4');
}

function drawWasher(c: Ctx, d: Decor): void {
  const { x, y } = d;
  shadow(c, x, y, 15, 3, 0.3);
  // Top-loading washing machine, the balcony classic.
  block(c, x - 13, y - 24, 26, 24, '#f4f5f2', '#ffffff', '#c7c9c4');
  rect(c, 'rgba(0,0,0,0.08)', x - 12, y - 6, 24, 5);
  block(c, x - 13, y - 30, 26, 8, '#e9ebe7', '#ffffff', '#c7c9c4');
  // Glass lid and control panel.
  block(c, x - 10, y - 29, 14, 6, '#9fc8dc', '#cfe8f2', '#6d9cb3');
  rect(c, 'rgba(255,255,255,0.6)', x - 9, y - 28.4, 5, 0.8);
  block(c, x + 5, y - 29, 6, 6, '#d6dad6', undefined, undefined);
  ellipse(c, '#3a8fd9', x + 8, y - 26, 1.4, 1.4);
  rect(c, '#e74c3c', x + 6, y - 24.4, 1, 0.8);
  rect(c, '#2ecc71', x + 8, y - 24.4, 1, 0.8);
  // A laundry basket beside it.
  block(c, x + 9, y - 9, 8, 9, '#4fb0d8', '#7ccaea', '#2d86ad');
  for (let i = 0; i < 4; i++) rect(c, 'rgba(255,255,255,0.4)', x + 10 + i * 2, y - 7, 0.6, 5);
  rect(c, '#f3a7b8', x + 10, y - 11, 6, 2);
}

function leafy(c: Ctx, x: number, y: number, r: number, col: string, sw: number, t: number): void {
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2 + 0.3;
    const lx = x + Math.cos(a) * r * 0.8 + sw * (0.4 + 0.2 * Math.sin(t * 3 + i));
    const ly = y - r * 0.6 + Math.sin(a) * r * 0.5;
    ellipse(c, shade(col, (i % 3) * 0.08 - 0.08), lx, ly, r * 0.55, r * 0.4);
  }
  ellipse(c, shade(col, 0.15), x + sw * 0.5, y - r * 0.9, r * 0.45, r * 0.35);
}

function drawPots(c: Ctx, d: Decor, t: number): void {
  const { x, y } = d;
  const sw = wind.at(x, y) * 1.4 + Math.sin(t * 1.9) * 0.4;
  // Big terracotta pot with aloe.
  shadow(c, x - 10, y, 8, 2.4);
  block(c, x - 17, y - 9, 14, 9, '#c0643a', '#dc8256', '#8e4426');
  rect(c, '#a6532f', x - 18, y - 10, 16, 2);
  for (let i = 0; i < 6; i++) {
    const a = -Math.PI / 2 + (i - 2.5) * 0.38;
    c.strokeStyle = i % 2 ? '#6fa65a' : '#86bb6c';
    c.lineWidth = 2.2 - (i % 2) * 0.5;
    c.lineCap = 'round';
    c.beginPath();
    c.moveTo(x - 10, y - 10);
    c.quadraticCurveTo(x - 10 + Math.cos(a) * 5, y - 14, x - 10 + Math.cos(a) * 9 + sw * 0.6, y - 10 + Math.sin(a) * 12);
    c.stroke();
  }
  c.lineCap = 'butt';
  // Basil (九層塔) in a blue glazed pot.
  shadow(c, x + 4, y, 6, 2);
  block(c, x - 1, y - 8, 10, 8, '#2f6fbf', '#4f8fd9', '#1f4f8f');
  leafy(c, x + 4, y - 9, 5, '#3f8f3a', sw, t);
  for (let i = 0; i < 3; i++) ellipse(c, '#b48ad6', x + 2 + i * 2 + sw * 0.6, y - 18 - (i % 2) * 2, 0.8, 1.6);
  // Chili pepper with red pods.
  shadow(c, x + 15, y, 5, 1.8);
  block(c, x + 11, y - 7, 8, 7, '#e9dcc4', '#f6eedf', '#bfae90');
  leafy(c, x + 15, y - 8, 4, '#4f9a44', sw, t + 1);
  for (let i = 0; i < 3; i++) block(c, x + 12 + i * 2.5 + sw * 0.5, y - 12 + (i % 2) * 2, 1.4, 3, '#e23b2a');
}

function drawStool(c: Ctx, d: Decor): void {
  const { x, y } = d;
  // The red plastic stool every Taiwanese family owns.
  shadow(c, x, y, 7, 2);
  rect(c, '#b8261c', x - 6, y - 8, 2, 8);
  rect(c, '#b8261c', x + 4, y - 8, 2, 8);
  rect(c, '#9a1f17', x - 4, y - 3, 8, 1.2);
  block(c, x - 7, y - 11, 14, 4, '#e0392b', '#f26a5b', '#b8261c');
  ellipse(c, '#9a1f17', x, y - 9.5, 2, 0.8);
}

function drawHerb(c: Ctx, d: Decor, t: number): void {
  const { x, y } = d;
  const sw = wind.at(x, y) * 1.2 + Math.sin(t * 1.6 + 2) * 0.4;
  // A money plant (金錢樹) by the door, for good luck.
  shadow(c, x, y, 7, 2.2);
  block(c, x - 6, y - 8, 12, 8, '#ecebe6', '#ffffff', '#bdbcb6');
  rect(c, '#e0392b', x - 4, y - 5, 8, 1.4);
  for (let k = 0; k < 4; k++) {
    const bx = x - 4.5 + k * 3;
    c.strokeStyle = '#4f7a3a';
    c.lineWidth = 0.8;
    c.beginPath();
    c.moveTo(x, y - 8);
    c.lineTo(bx + sw * (0.4 + k * 0.15), y - 16 - (k % 2) * 4);
    c.stroke();
    for (let l = 0; l < 4; l++) ellipse(c, l % 2 ? '#2f7a3a' : '#3f9a48', bx + sw * (0.3 + k * 0.1) + (l % 2 ? 1.2 : -1.2), y - 11 - l * 2 - (k % 2) * 2, 1.6, 1);
  }
}

// --- Garden planters -------------------------------------------------------------------

function todayNum(): number {
  return S().dayIndex;
}

function sparkle(c: Ctx, x: number, y: number, t: number, k: number): void {
  const a = Math.max(0, Math.sin(t * 3 + k * 2.1));
  if (a < 0.2) return;
  c.fillStyle = `rgba(255,248,190,${a})`;
  const s = 0.6 + a * 1.4;
  c.fillRect(x - 0.3, y - s, 0.6, s * 2);
  c.fillRect(x - s, y - 0.3, s * 2, 0.6);
}

function stem(c: Ctx, x: number, y: number, h: number, sw: number, col = '#4f8f3a', w = 1): [number, number] {
  c.strokeStyle = col;
  c.lineWidth = w;
  c.beginPath();
  c.moveTo(x, y);
  c.quadraticCurveTo(x + sw * 0.3, y - h * 0.6, x + sw, y - h);
  c.stroke();
  return [x + sw, y - h];
}

function leaf(c: Ctx, x: number, y: number, dir: number, len: number, col: string): void {
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(x, y);
  c.quadraticCurveTo(x + dir * len * 0.5, y - len * 0.6, x + dir * len, y - len * 0.1);
  c.quadraticCurveTo(x + dir * len * 0.5, y + len * 0.25, x, y);
  c.fill();
}

function strawberry(c: Ctx, x: number, y: number, st: number, sw: number, t: number, k: number): void {
  if (st === 1) {
    leaf(c, x, y, -1, 2.5, '#6abf4b');
    leaf(c, x, y, 1, 2.5, '#7ccf5a');
    return;
  }
  const r = st === 2 ? 2.6 : 4;
  // Trifoliate leaves on short stalks, a low rosette.
  for (let i = 0; i < (st === 2 ? 3 : 5); i++) {
    const a = -Math.PI / 2 + (i - (st === 2 ? 1 : 2)) * 0.6;
    const lx = x + Math.cos(a) * r + sw * 0.5, ly = y - 1 + Math.sin(a) * r * 0.8;
    c.strokeStyle = '#4f8f3a';
    c.lineWidth = 0.5;
    c.beginPath();
    c.moveTo(x, y);
    c.lineTo(lx, ly);
    c.stroke();
    for (const o of [-1, 0, 1]) ellipse(c, o ? '#3f8f3a' : '#55a845', lx + o * 1.3, ly - (o ? 0 : 0.8), 1.4, 1.1);
    rect(c, 'rgba(255,255,255,0.18)', lx - 0.5, ly - 1.2, 1, 0.3);
  }
  if (st === 3) {
    for (const o of [-2.5, 2.5]) {
      ellipse(c, '#ffffff', x + o + sw * 0.4, y - 3.5, 1.2, 1.1);
      ellipse(c, '#f2d24c', x + o + sw * 0.4, y - 3.5, 0.45, 0.45);
    }
  } else if (st === 4) {
    for (const [o, dy] of [[-3, -0.5], [0.5, 0.8], [3.5, -0.2]] as const) {
      const bx = x + o + sw * 0.3, by = y + dy;
      c.fillStyle = '#e02b3a';
      c.beginPath();
      c.moveTo(bx - 1.5, by - 1.2);
      c.lineTo(bx + 1.5, by - 1.2);
      c.lineTo(bx, by + 1.8);
      c.closePath();
      c.fill();
      rect(c, '#3f8f3a', bx - 1, by - 1.8, 2, 0.7);
      rect(c, '#ffe28a', bx - 0.5, by - 0.5, 0.3, 0.3);
      rect(c, '#ffe28a', bx + 0.4, by + 0.2, 0.3, 0.3);
      rect(c, 'rgba(255,255,255,0.6)', bx - 1, by - 1, 0.5, 0.5);
    }
    sparkle(c, x - 4, y - 6, t, k);
  }
}

function tomato(c: Ctx, x: number, y: number, st: number, sw: number, t: number, k: number): void {
  if (st === 1) {
    leaf(c, x, y - 1, -1, 2.2, '#6abf4b');
    leaf(c, x, y - 1, 1, 2.2, '#7ccf5a');
    stem(c, x, y, 1.2, 0, '#6abf4b', 0.6);
    return;
  }
  const h = st === 2 ? 6 : 16;
  if (st >= 3) {
    // Bamboo stake with a tie.
    rect(c, '#c9a86a', x + 2.5, y - h - 3, 0.9, h + 3);
    rect(c, '#f2e6cf', x + 2, y - h * 0.6, 2, 0.6);
  }
  const [tx, ty] = stem(c, x, y, h, sw, '#4f8f3a', st >= 3 ? 1.1 : 0.8);
  const n = st === 2 ? 2 : 4;
  for (let i = 0; i < n; i++) {
    const f = (i + 1) / (n + 1);
    const px = x + sw * f * f, py = y - h * f;
    leaf(c, px, py, i % 2 ? 1 : -1, st === 2 ? 2.8 : 4, i % 2 ? '#3f8f3a' : '#55a845');
  }
  leaf(c, tx, ty, -1, 2.4, '#55a845');
  leaf(c, tx, ty, 1, 2.4, '#3f8f3a');
  if (st === 3) {
    ellipse(c, '#f2d24c', tx - 2, ty + 4, 0.9, 0.9);
    ellipse(c, '#f2d24c', tx + 2.5, ty + 7, 0.9, 0.9);
    ellipse(c, '#8cc65a', x + sw * 0.4 - 2, y - h * 0.45, 1.6, 1.6);
  } else if (st === 4) {
    for (const [ox, f] of [[-2.4, 0.42], [2.2, 0.55], [-1.6, 0.75]] as const) {
      const bx = x + sw * f * f + ox, by = y - h * f + 1.5;
      ellipse(c, '#9c1f14', bx, by + 0.3, 2.3, 2.1);
      ellipse(c, '#e2392a', bx, by, 2.2, 2);
      ellipse(c, 'rgba(255,255,255,0.55)', bx - 0.8, by - 0.8, 0.6, 0.5);
      rect(c, '#3f8f3a', bx - 0.8, by - 2.2, 1.6, 0.6);
    }
    sparkle(c, tx + 3, ty + 2, t, k);
  }
}

function sunflower(c: Ctx, x: number, y: number, st: number, sw: number, t: number, k: number): void {
  if (st === 1) {
    leaf(c, x, y - 1, -1, 2.6, '#7ccf5a');
    leaf(c, x, y - 1, 1, 2.6, '#6abf4b');
    stem(c, x, y, 1.5, 0, '#6abf4b', 0.7);
    return;
  }
  const h = st === 2 ? 8 : st === 3 ? 16 : 21;
  const [tx, ty] = stem(c, x, y, h, sw * (st === 2 ? 0.5 : 1.2), '#5a9a3a', st === 2 ? 0.9 : 1.4);
  const n = st === 2 ? 2 : 4;
  for (let i = 0; i < n; i++) {
    const f = (i + 1) / (n + 1.5);
    leaf(c, x + sw * f * f * 1.2, y - h * f, i % 2 ? 1 : -1, st === 2 ? 3 : 5, i % 2 ? '#3f8f3a' : '#55a845');
  }
  if (st === 2) return;
  if (st === 3) {
    ellipse(c, '#4f8f3a', tx, ty, 2.4, 2.2);
    for (let i = 0; i < 5; i++) rect(c, '#f2d24c', tx - 2 + i, ty - 2.6, 0.6, 1);
    return;
  }
  // Ripe: a big smiling face of petals turned towards you.
  const ph = Math.sin(t * 1.3 + k) * 0.15;
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2 + ph;
    c.fillStyle = i % 2 ? '#f6c21c' : '#ffd84a';
    c.beginPath();
    c.ellipse(tx + Math.cos(a) * 4, ty + Math.sin(a) * 3.6, 2.4, 1.1, a, 0, Math.PI * 2);
    c.fill();
  }
  ellipse(c, '#5a3418', tx, ty, 3, 2.7);
  ellipse(c, '#7a4a24', tx - 0.5, ty - 0.5, 2, 1.8);
  for (let i = 0; i < 6; i++) rect(c, 'rgba(255,220,120,0.5)', tx - 1.5 + (i % 3), ty - 1 + Math.floor(i / 3), 0.4, 0.4);
  sparkle(c, tx + 6, ty - 3, t, k);
}

const SPOTS: Record<string, number[]> = { strawberry: [6, 15, 24], tomato: [9, 21], sunflower: [9, 21] };

function drawPlot(i: number) {
  return (c: Ctx, r: Rect, t: number): void => {
    const s = hasGame() ? S() : null;
    const p: GardenPlot = s?.garden[i] ?? { crop: null, water: 0, lastDay: -1 };
    const col = i % 3;
    const x0 = r.x - 1, x1 = r.x + r.w + 1;
    const top = r.y - 3, front = r.y + 3, bottom = r.y + r.h;
    if (col === 0) shadow(c, r.x + 47, bottom, 50, 3.2, 0.3);
    // Wooden planter: the rim, the soil inside and the front boards.
    rect(c, '#5e3a22', x0, top - 0.5, x1 - x0, bottom - top + 1);
    const wet = !!p.crop && s !== null && (p.lastDay === todayNum() || rainedToday());
    const soil = wet ? '#3e2717' : '#7a5536';
    rect(c, soil, x0 + (col === 0 ? 1.5 : 0), top + 1, x1 - x0 - (col === 0 ? 1.5 : 0) - (col === 2 ? 1.5 : 0), front - top - 1);
    for (let k = 0; k < 10; k++) {
      const sx = x0 + 2 + hash(i, k, 1) * (x1 - x0 - 4), sy = top + 1.5 + hash(i, k, 2) * (front - top - 2.5);
      rect(c, wet ? 'rgba(160,200,230,0.35)' : 'rgba(255,230,190,0.2)', sx, sy, 0.8, 0.5);
    }
    if (!p.crop) for (let k = 0; k < 3; k++) rect(c, 'rgba(0,0,0,0.18)', x0 + 4 + k * 9, top + 2.5, 6, 0.6);
    block(c, x0, front, x1 - x0, bottom - front, '#a8723f', '#c48a52', '#7a4e2a');
    rect(c, 'rgba(0,0,0,0.18)', x0, front + (bottom - front) / 2, x1 - x0, 0.5);
    rect(c, '#c48a52', x0, top - 0.5, x1 - x0, 1.2);
    if (col === 0) block(c, x0 - 1, top - 1, 2.5, bottom - top + 1, '#8e5a30', '#b07440', '#5e3a22');
    if (col === 2) block(c, x1 - 1.5, top - 1, 2.5, bottom - top + 1, '#8e5a30', '#b07440', '#5e3a22');
    if (col === 1) {
      rect(c, '#7a4e2a', x0 - 0.3, front, 0.6, bottom - front);
      rect(c, '#7a4e2a', x1 - 0.3, front, 0.6, bottom - front);
    }
    // A little name stake with the crop icon colour.
    if (p.crop) {
      const tag = { strawberry: '#e02b3a', tomato: '#e2392a', sunflower: '#f6c21c' }[p.crop];
      rect(c, '#e8dcc0', x1 - 5, front - 4, 0.8, 5);
      block(c, x1 - 7, front - 7, 5, 3.5, '#f6eedf', undefined, undefined);
      ellipse(c, tag, x1 - 4.5, front - 5.3, 1, 1);
    }
    if (s && arePlantsProtected(s)) {
      // Typhoon prep: a blue tarp tied over the planter.
      c.fillStyle = '#2f6fbf';
      c.beginPath();
      c.moveTo(x0, front);
      c.quadraticCurveTo((x0 + x1) / 2, top - 10, x1, front);
      c.closePath();
      c.fill();
      rect(c, 'rgba(255,255,255,0.25)', x0 + 4, top - 2, x1 - x0 - 8, 0.8);
      for (const f of [0.3, 0.7]) rect(c, '#f2e6cf', x0 + (x1 - x0) * f, top - 6, 0.6, front - top + 6);
      return;
    }
    if (!p.crop) return;
    const st = stage(p);
    const draw = { strawberry, tomato, sunflower }[p.crop];
    SPOTS[p.crop].forEach((dx, k) => {
      const px = r.x + dx, py = front - 1;
      const sw = (wind.at(px, py) * 1.6 + Math.sin(t * 1.7 + px * 0.3) * 0.6) * (st >= 3 ? 1 : 0.4);
      draw(c, px, py, st, sw, t, i * 3 + k);
    });
  };
}

function drawTap(c: Ctx, r: Rect, t: number): void {
  const x = r.x, y = r.y + r.h;
  // Pipe out of the wall, a brass tap, a blue bucket and the green watering can.
  rect(c, '#9aa0a6', x - 2, y - 22, 4, 2);
  rect(c, '#9aa0a6', x - 2, y - 26, 2, 6);
  block(c, x + 1, y - 23, 4, 3, '#c9a24a', '#e8c86a', '#9a7a2a');
  block(c, x + 2, y - 26, 2, 2, '#d0453a', '#ec6a5c', '#9c2a22');
  rect(c, '#c9a24a', x + 4, y - 21, 1, 2);
  const drip = (t * 0.8) % 1;
  if (drip < 0.6) ellipse(c, 'rgba(160,210,240,0.85)', x + 4.5, y - 18 + drip * 8, 0.5, 0.7);
  shadow(c, x + 5, y, 7, 2);
  block(c, x, y - 9, 10, 9, '#2f86d0', '#5aa8e6', '#1f5f9e');
  ellipse(c, '#1f5f9e', x + 5, y - 9, 5, 1.4);
  ellipse(c, '#8ccfee', x + 5, y - 9, 4, 1);
  // Watering can.
  const full = hasGame() && canLeft() > 0;
  const cx = x + 15, cy = y;
  shadow(c, cx, cy, 6, 1.8);
  block(c, cx - 5, cy - 7, 9, 7, '#3f9a48', '#5aba60', '#2a6f32');
  c.strokeStyle = '#2a6f32';
  c.lineWidth = 1;
  c.beginPath();
  c.moveTo(cx + 3, cy - 4);
  c.lineTo(cx + 8, cy - 9);
  c.stroke();
  ellipse(c, '#2a6f32', cx + 8.5, cy - 9.3, 1.3, 1);
  c.beginPath();
  c.arc(cx - 0.5, cy - 7, 3, Math.PI, 0);
  c.stroke();
  if (full) {
    ellipse(c, '#8ccfee', cx - 0.5, cy - 7, 3.4, 0.9);
    sparkle(c, cx + 8.5, cy - 11, t, 5);
  }
}

registerPropDrawers({
  objects: {
    ...Object.fromEntries(Array.from({ length: 9 }, (_, i) => [`fam_plot${i}`, drawPlot(i)])),
    fam_tap: drawTap,
  },
  decor: (c, d, t) => {
    switch (d.kind) {
      case 'fam_view':
        drawView(c, t);
        return true;
      case 'fam_railS':
        cached(c, 'front', VX0, FRONT_Y - 16, VX1 - VX0, 390, paintFront);
        return true;
      case 'fam_laundry':
        drawLaundry(c, d, t);
        return true;
      case 'fam_washer':
        drawWasher(c, d);
        return true;
      case 'fam_pots':
        drawPots(c, d, t);
        return true;
      case 'fam_stool':
        drawStool(c, d);
        return true;
      case 'fam_herb':
        drawHerb(c, d, t);
        return true;
    }
    return false;
  },
  animatedObjects: [...Array.from({ length: 9 }, (_, i) => `fam_plot${i}`), 'fam_tap'],
  animatedDecor: ['fam_view', 'fam_laundry', 'fam_pots', 'fam_herb'],
});

void OUTLINE;
